import { useEffect } from 'react';
import { useMotionValue, useReducedMotion, useSpring } from 'framer-motion';

/**
 * Decorative pointer parallax. Returns spring-damped motion values in roughly
 * [-1, 1] tracking the cursor's offset from the viewport center. Consumers map
 * them to small translate/rotate ranges at different depths via `useTransform`.
 *
 * Spring (not raw position) is what makes it feel alive instead of mechanical
 * (emil-design-eng: tie decorative motion to a spring, never to raw input).
 * Inert when the user prefers reduced motion or lacks a fine pointer (touch).
 */
export function usePointerParallax() {
  const reduced = useReducedMotion();
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const spring = { stiffness: 55, damping: 18, mass: 0.7 };
  const x = useSpring(rawX, spring);
  const y = useSpring(rawY, spring);

  useEffect(() => {
    if (reduced) return;
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const onMove = (e: PointerEvent) => {
      rawX.set((e.clientX / window.innerWidth) * 2 - 1);
      rawY.set((e.clientY / window.innerHeight) * 2 - 1);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [reduced, rawX, rawY]);

  return { x, y };
}
